import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/auth/AuthContext';
import { sendPresenceBeat, sendPresenceLeave, type PresenceRoster } from './presence.api';

/** A tab in view with recent input beats this often and reads as "here". */
export const BEAT_HERE_MS = 60_000;
/** A hidden or idle tab slows to this and reads as "away". */
export const BEAT_AWAY_MS = 5 * 60_000;
/** No pointer, key or wheel input for this long and a visible tab is idle. */
export const IDLE_MS = 10 * 60_000;
/** Wait for navigation to settle before announcing the new page. */
const PATH_SETTLE_MS = 1_500;
/**
 * Floor between two beats from one tab. Clicking through the rail, or
 * flicking between browser tabs, would otherwise send one request per
 * click, and each request costs the API a PostgREST round-trip (the JWT
 * revocation check).
 */
const MIN_GAP_MS = 10_000;

/** One id per page load. A reload sends `leave` for the old id on the way out. */
function newTabId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `t${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  }
}

/**
 * Heartbeat for the top bar's presence capsule. Mount ONCE, in the Shell:
 * the Shell survives navigation, so the timer does too, and a second mount
 * would double every request (the useNavBadges lesson, 2026-09-22).
 *
 * Returns the last roster received; `null` until the first beat lands, and
 * kept on screen through a failed beat rather than blanking the capsule.
 */
export function usePresence(pathname: string): PresenceRoster | null {
  const { user } = useAuth();
  // Agents are neither tracked nor shown the roster (server enforces it too).
  const enabled = !!user && user.role !== 'agent';
  const userId = user?.id ?? '';
  const [roster, setRoster] = useState<PresenceRoster | null>(null);

  const tabIdRef = useRef<string>('');
  if (!tabIdRef.current) tabIdRef.current = newTabId();
  const pathRef = useRef(pathname);
  const lastInputRef = useRef(Date.now());
  const lastBeatRef = useRef(0);
  const lastActiveRef = useRef<boolean | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const beatRef = useRef<() => void>(() => {});
  const scheduleRef = useRef<(delayMs: number) => void>(() => {});

  useEffect(() => {
    if (!enabled) {
      setRoster(null);
      return;
    }
    let disposed = false;
    let inflight: AbortController | null = null;
    const tabId = tabIdRef.current;

    const isActive = () =>
      document.visibilityState === 'visible' && Date.now() - lastInputRef.current < IDLE_MS;

    const schedule = (delayMs: number) => {
      if (disposed) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => beatRef.current(), Math.max(0, delayMs));
    };

    /** Beat now, or as soon as the floor allows. */
    const beatSoon = (settleMs = 0) => {
      const wait = Math.max(settleMs, lastBeatRef.current + MIN_GAP_MS - Date.now());
      schedule(wait);
    };

    const beat = () => {
      if (disposed) return;
      const active = isActive();
      lastBeatRef.current = Date.now();
      lastActiveRef.current = active;
      inflight?.abort();
      const ctl = new AbortController();
      inflight = ctl;
      sendPresenceBeat({ tabId, path: pathRef.current, active }, ctl.signal)
        .then((r) => {
          if (!disposed) setRoster(r);
        })
        .catch(() => {
          /* keep the last roster; the next beat retries */
        })
        .finally(() => {
          if (inflight === ctl) inflight = null;
        });
      schedule(active ? BEAT_HERE_MS : BEAT_AWAY_MS);
    };

    beatRef.current = beat;
    scheduleRef.current = beatSoon;

    // Flip here ↔ away promptly instead of waiting out a five-minute beat.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') lastInputRef.current = Date.now();
      if (isActive() !== lastActiveRef.current) beatSoon();
    };
    const onInput = () => {
      const wasIdle = Date.now() - lastInputRef.current >= IDLE_MS;
      lastInputRef.current = Date.now();
      if (wasIdle && lastActiveRef.current === false) beatSoon();
    };
    const onPageHide = () => sendPresenceLeave(tabId);

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    for (const ev of ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const)
      window.addEventListener(ev, onInput, { passive: true, capture: true });

    beat();

    return () => {
      disposed = true;
      inflight?.abort();
      if (timerRef.current) clearTimeout(timerRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      for (const ev of ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const)
        window.removeEventListener(ev, onInput, { capture: true });
      // Logging out / losing the grant: leave now rather than age out.
      sendPresenceLeave(tabId);
    };
    // userId: a sign-out and sign-in as someone else in the same tab must
    // leave under the old identity and beat under the new one.
  }, [enabled, userId]);

  // Announce a new page once navigation settles. The ref is written here,
  // not during render (a render can be discarded and replayed). The first
  // page is covered by the initial beat above.
  useEffect(() => {
    if (pathRef.current === pathname) return;
    pathRef.current = pathname;
    if (enabled) scheduleRef.current(PATH_SETTLE_MS);
  }, [pathname, enabled]);

  return enabled ? roster : null;
}
